import PageHeader from '@Guidelines/Layout/PageHeader';
import PageContainer from '@Guidelines/Layout/PageContainer';
import useSubjectTypes from '@UI/Hooks/useSubjectTypes';
import CreateSubjectSidebar from '@UI/Pages/Subjects/Sidebars/CreateSubjectSidebar';
import Filters from '@UI/Pages/Subjects/SubjectList/Filters';
import SubjectFilterView from '@UI/Pages/Subjects/SubjectList/Filters/SubjectFilterView';
import subjectFiltersSignal, {
	setSubjectFilters,
} from '@UI/Pages/Subjects/SubjectList/Filters/Signals/subjectFiltersSignal';
import SubjectsListContent from '@UI/Pages/Subjects/SubjectList/SubjectsListContent';
import { openCreateSubjectSidebar } from '@UI/Pages/Subjects/Signals/createSubjectSidebarSignal';
import SubjectsNotifications from '@UI/Pages/Subjects/SubjectsNotifications';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { BreadcrumbActions } from '@verclam/breadcrumbs';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

/**
 * Assembles the page of the dossier type named by the route: header, `Filters` (they read their own
 * signal), the data-owning `SubjectsListContent`, the creation panel and notifications. The filters
 * belong to one type: when the route changes type, they start over from the new type's defaults, and
 * the content only mounts once they do. The header's count reads the list `SubjectsListContent`
 * populates, through a plain `useSelector`.
 */
const SubjectListPage: React.FC = () => {
	const { subjectType: typeKey = '' } = useParams<{ subjectType: string }>();
	const { types } = useSubjectTypes();
	const type = types?.findByKey(typeKey) ?? null;
	const typeId = type?.id ?? null;
	const filters = useSignalValue(subjectFiltersSignal);
	const subjectsList = useSelector((state) => state.subjectsStore.subjects);

	React.useEffect(() => {
		if (typeId !== null && subjectFiltersSignal.peek().typeId !== typeId) {
			setSubjectFilters(SubjectFilterView.createDefault(typeId));
		}
	}, [typeId]);

	const description = React.useMemo(() => {
		if (subjectsList === null || subjectsList.typeId !== typeId) {
			return undefined;
		}

		const count = subjectsList.totalRecords;

		return `${count} dossier${count > 1 ? 's' : ''}`;
	}, [subjectsList, typeId]);

	return (
		<PageContainer scrollY={'scroll'}>
			<ConditionalLoader data={type}>
				{(currentType) => (
					<React.Fragment>
						<BreadcrumbActions>
							<Button
								type="button"
								text
								label={currentType.wording.createAction}
								icon={<Plus size={16} />}
								onClick={openCreateSubjectSidebar}
							/>
						</BreadcrumbActions>
						<PageHeader title={currentType.wording.plural} description={description} />
						<Filters type={currentType} />
						<ConditionalLoader data={filters.typeId === currentType.id}>
							<SubjectsListContent type={currentType} />
						</ConditionalLoader>
						<CreateSubjectSidebar type={currentType} />
					</React.Fragment>
				)}
			</ConditionalLoader>
			<SubjectsNotifications />
		</PageContainer>
	);
};

export default SubjectListPage;
